// @flow

// Selectors
import selectedJobAndGrid$ from 'hfty-soil-jobs/src/selectors/selectedJobAndGrid'
import selectedField$ from 'hfty-map/src/selectors/selectedField'
import operation$ from 'hfty-soil-jobs-dom/src/selectors/operation'
import featuresToGrid$ from './featuresToGrid'
import { currentUserUnits$ } from 'hfty-users'

// Libs
import { createSelector } from 'reselect'
import _size from 'lodash/size'
import _get from 'lodash/get'
import _forEach from 'lodash/forEach'
import _filter from 'lodash/filter'
import _map from 'lodash/map'
import _reduce from 'lodash/reduce'
import _head from 'lodash/head'
import _isEqual from 'lodash/isEqual'
import turfKinks from '@turf/kinks'
import turfUnkink from '@turf/unkink-polygon'
import squareGrid from '@turf/square-grid'
import bbox from '@turf/bbox'
import bboxPolygon from '@turf/bbox-polygon'
import transformTranslate from '@turf/transform-translate'
import transformRotate from '@turf/transform-rotate'
import transformScale from '@turf/transform-scale'
import distance from '@turf/distance'
import rhumbBearing from '@turf/rhumb-bearing'
import centerOfObject from '@turf/center'
import inside from '@turf/inside'
import { point, multiPolygon, polygon, featureCollection } from '@turf/helpers'
import { intersection as polyClip } from 'polygon-clipping'
import bboxSquare from '@turf/square'

// Constants
import { GRID_UNIT_HECTARES, GRID_UNIT_ACRES, GRID_UNIT_MILES, GRID_UNIT_KILOMETERS } from 'hfty-model-grid'
import { PROP_US_CUSTOMARY } from 'fc-model-users'
import { DEFAULT_GRID_TRANSFORMATIONS, DEFAULT_BOX_SCALE } from 'hfty-grid/src/constants'

export default createSelector(
  [
    featuresToGrid$,
    operation$,
    selectedJobAndGrid$,
    selectedField$,
    currentUserUnits$,
  ],
  (
    selectedFeatures: Object,
    operation: Object,
    selectedJob: Object,
    field: Object,
    currentUserUnits: string,
  ): Object => {
    const hasSelectedFeatures = _size(selectedFeatures) > 0
    const hasGridFeatures = _get(selectedJob, 'grid.features.length') > 0
    let features = selectedFeatures
    let gridData = {}
    let shapeData = {}

    if (!hasSelectedFeatures || (!operation && !hasGridFeatures)) {
      return null
    }

    // Set Grid Data Source. If operation specified we want the props off of operation
    // Operation -> Add/Edit
    if (operation) {
      // shape of the current field (could have added new features)
      shapeData = field.shape.shapeData
      // off of the operation, currently modifying the operation
      gridData = operation.model.gridData.grid
    } else if (hasGridFeatures) {
      let shapeProperties = {}
      shapeData = selectedJob.grid

      _forEach(shapeData.features, ({properties}: Object) => {
        gridData[properties.featureIndex] = properties
        shapeProperties = properties
      })

      gridData = {
        ...gridData,
        transformations: shapeProperties || DEFAULT_GRID_TRANSFORMATIONS,
        units: currentUserUnits === PROP_US_CUSTOMARY ? GRID_UNIT_ACRES : GRID_UNIT_HECTARES,
      }

      if (shapeData.features.length === selectedFeatures.length) {
        features = shapeData.features
      }
    }

    return _filter(_map(features, (shape: Object, index: number): Object => {
      if (shape) {
        const kinks = turfKinks(shape)
        const hasKinks = _get(kinks, 'features.length', 0) > 0
        let boundary

        if (hasKinks) {
          boundary = cleanShape(shape)
          boundary = turfUnkink(boundary)
        } else {
          boundary = shape
        }

        const { featureIndex, isLocked } = shape.properties

        let data = operation &&
          (!gridData[featureIndex] || !isLocked) &&
          selectedFeatures.length > 1 ? gridData.transformations : gridData[featureIndex]

        if (shapeData.features.length !== selectedFeatures.length) {
          data = gridData.transformations
        }

        const _squareGrid = calculateGrid(data, shapeData, gridData.units)
        const clippedGrid = clipSquareGrid(_squareGrid, boundary)

        const clippedGridShapes = parseShapes(clippedGrid)
        const squareGridShapes = parseShapes(_squareGrid)

        const pointBounds = getGridPointBounds(featureCollection([shape]))
        const gridPoints = gridPointsInsideBounds(squareGridShapes, pointBounds)

        return {
          squareGrid: _squareGrid,
          gridPoints: gridPoints,
          shapes: clippedGridShapes,
          gridShapes: squareGridShapes,
          isLocked,
        }
      }

      return null
    }))
  }
)

function clipSquareGrid (_squareGrid: Object, boundary: Object): Object {
  // turfJS may return bounds as a collection but it will have one set of coords
  const bounds = boundary.type === 'FeatureCollection'
    ? boundary.features[0].geometry.coordinates
    : boundary.geometry.coordinates

  // Get box lines intersecting with boundary
  const resultPoly = []
  for (const index in _squareGrid.features) {
    const getClipped = polyClip(bounds, _squareGrid.features[index].geometry.coordinates)
    if (getClipped.length > 0) {
      for (const innerIndex in getClipped) {
        resultPoly.push(getClipped[innerIndex])
      }
    }
  }
  // Go through unpacked features and build new square grid
  const newFeatures = []
  for (const index in resultPoly) {
    const newFeature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: resultPoly[index],
      },
    }
    newFeatures.push(newFeature)
  }

  return {
    type: 'FeatureCollection',
    features: newFeatures,
  }
}

function cleanShape (shape: Object): Object {
  const featureCoords = _head(_get(shape, ['geometry', 'coordinates'], []))
  const stringifiedCoords =
    featureCoords.map((coords: Array): Array => JSON.stringify(coords)).slice(1, featureCoords.length)

  const cleanedFeatureCoords = featureCoords.map((coords: Object, index: number): Object => {
    const firstIndex = stringifiedCoords.indexOf(JSON.stringify(coords))
    const lastIndex = stringifiedCoords.lastIndexOf(JSON.stringify(coords))
    if (!(_isEqual(firstIndex, lastIndex) || (index === 0 || index === featureCoords.length))) {
      coords[0] += Number(`0.00000${index * 2}`)
      coords[1] += Number(`0.00000${index * 2}`)
    }
    return coords
  })

  return {
    ...shape,
    geometry: {
      ...shape.geometry,
      coordinates: [cleanedFeatureCoords],
    },
  }
}

function calculateGrid (gridData: Object, shapeData: Object, units: string): Object {
  const { size, offset, rotation } = gridData
  const unitOfDistance = units === GRID_UNIT_HECTARES ? GRID_UNIT_MILES : GRID_UNIT_KILOMETERS

  // bbox of field
  let box = bbox(shapeData)
  // Scale bounds of box
  const scale = DEFAULT_BOX_SCALE
  const polygonOfBbox = bboxPolygon(box)
  box = bbox(transformScale(polygonOfBbox, scale, {origin: 'center'}))
  // thicken bbox
  box = bboxSquare(box)
  // get square grid single  cell width
  const cellWidth = calcCellWidth(size, units)
  let fieldGrid = squareGrid(box, cellWidth, { units: unitOfDistance })
  const center = centerOfObject(shapeData)

  // Fall back for empty/undefined offset, use first point in feature
  if (rotation) {
    // Apply Rotation
    fieldGrid = transformRotate(fieldGrid, rotation, {
      pivot: center,
      mutate: true,
    })
  }
  if (offset) {
    const fieldGridOffset = point([offset.longitude, offset.latitude])
    const offsetDistance = distance(center, fieldGridOffset, unitOfDistance)
    const offsetBearing = rhumbBearing(center, fieldGridOffset)
    // Apply Offset
    fieldGrid = transformTranslate(fieldGrid, offsetDistance, offsetBearing, {
      units: unitOfDistance,
      zTranslation: 0,
      mutate: true,
    })
  }

  return fieldGrid
}

function parseFeature (feature: Object): Object {
  let shape = {
    positions: [],
    closePath: true,
    center: [],
  }
  for (let i = 0; i < feature.geometry.coordinates.length; i++) {
    const transform = (coordinate: Object): Array<number> => ([coordinate[1], coordinate[0]])
    shape.positions = _map(feature.geometry.coordinates[i], transform)
    const centerCoords = centerOfObject(feature).geometry.coordinates
    shape.center = [centerCoords[1], centerCoords[0]]
  }

  return shape
}

function parseShapes (grid: Object): Array<Object> {
  let shapes = []
  for (let i = 0; i < grid.features.length; i++) {
    shapes.push(parseFeature(grid.features[i]))
  }
  return shapes
}

function getGridPointBounds (shape: Object): Array<Object> {
  const bounds = []

  for (let i = 0; i < shape.features.length; i++) {
    const fieldBorder = multiPolygon(shape.features[i].geometry.coordinates)
    bounds.push(polygon(fieldBorder.geometry.coordinates))
  }

  return bounds
}

function gridPointsInsideBounds (gridShapes: Object, pointBounds: Array<Object>): Array<Object> {
  return _reduce(gridShapes, (acc: Array, gridShape: Object): Object => {
    const gridPoint = point([gridShape.center[1], gridShape.center[0]])

    for (let i = 0; i < pointBounds.length; i++) {
      if (inside(gridPoint, pointBounds[i])) {
        acc.push(gridShape.center)
        break
      } else {
        acc.push(null)
      }
    }

    return acc
  }, []
  )
}

function calcCellWidth (size: number, units: string): number {
  let km2 = null
  if (units === GRID_UNIT_HECTARES) {
    // conversion: hectares -> km2
    km2 = size * 0.01
  } else {
    // conversion: acres -> km2
    km2 = size * 0.00404685642
  }
  const cellWidth = Math.sqrt(km2)
  return cellWidth
}
